/**
 * ConfidenceBadge - Displays confidence percentage as a badge
 *
 * Atomic level: Molecule
 * Used in: RecordDetail, CaseWorkspaceTopBar, FOSOpinionPanel
 *
 * Displays confidence percentage with consistent styling across the platform.
 * Matches the pattern used in CaseWorkspaceTopBar and FOSOpinionPanel.
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../lib/utils';

const confidenceBadgeVariants = cva(
  'inline-flex items-center justify-center rounded border font-medium whitespace-nowrap',
  {
    variants: {
      size: {
        sm: 'px-2 py-0.5 text-figma-xs',
        md: 'px-3 py-1.5 text-figma-sm',
        lg: 'px-3 py-1.5 text-figma-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

interface ConfidenceBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof confidenceBadgeVariants> {
  confidence: number; // 0-100
}

const ConfidenceBadge = React.forwardRef<HTMLSpanElement, ConfidenceBadgeProps>(
  ({ className, size, confidence, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          confidenceBadgeVariants({ size }),
          'bg-blue-50 text-blue-700 border-blue-200',
          className
        )}
        {...props}
      >
        {confidence}% confidence
      </span>
    );
  }
);

ConfidenceBadge.displayName = 'ConfidenceBadge';

export { ConfidenceBadge, confidenceBadgeVariants };


